import React, { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import config from "../../../config/config";
import axios from "axios";
import ProfileUpdateSuccessModal from "./ProfileUpdateSuccessModal";
import Dropdown from 'react-dropdown-select';
import { useMediaQuery } from "react-responsive";
import monthYearData from "./data/month_year_data.json";
import genderOptions from "../../../data/genderOptions.json";
import relationshipOptions from "./data/relationshipOptions.json";
import {
  customDropdownStyles,
  dropdownContentRenderer,
  noBorderItemRenderer
} from "../../../util_functions/helpers";
import { useUser } from "../../../context/UserContext";

const PersonalDetailsComponent = () => {
  const { clientUser, setClientUser } = useUser();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [address, setAddress] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selection, setSelection] = useState("");
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [accountManagerForms, setAccountManagerForms] = useState([]);
  const [age, setAge] = useState("");
  const [errorMessages, setErrorMessages] = useState({});

  const baseURL = config.API_BASE_URL;
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const lastFormRef  = useRef(null);

  useEffect(() => {
    if (clientUser) {
      console.log("Client User from Personal Details: ", clientUser);
      setFormData(clientUser);
    }
  }, [clientUser]);

  useEffect(() => {
    if (accountManagerForms.length > 1) {
      lastFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [accountManagerForms.length]);

  const setFormData = (user) => {
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setEmail(user.email || "");
    setContactPhone(user.mobileNumber || "");
    setAddress(user.primaryLocation || "");
    setSelection(user.gender || "");

    if (user.dateOfBirth) {
      const [year, month, day] = user.dateOfBirth.split("-");
      setYear(year);
      setMonth(month);
      setDay(day);
      computeAge(day, month, year);
    }

    if (user.accountManagerDetails && user.accountManagerDetails.length > 0) {
      const accountManagers = user.accountManagerDetails.map((manager) => {
        const [year, month, day] = manager.dateOfBirth.split("-");
        return {
          ...manager,
          day,
          month,
          year,
        };
      });
      setAccountManagerForms(accountManagers);
    }
  };

  useEffect(() => {
    if (day && month && year) {
      computeAge(day, month, year);
    }
  }, [day, month, year]);

  const computeAge = (day, month, year) => {
    if (!day || !month || !year) return "";

    const today = new Date();
    const birthDate = new Date(year, month - 1, day);
    let age = today.getFullYear() - birthDate.getFullYear();

    if (
      today.getMonth() < birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    setAge(age);
  };

  const addAccountManagerForm = () => {
    if (accountManagerForms.length < 3) {
      setAccountManagerForms([
        ...accountManagerForms,
        {
          firstName: "",
          lastName: "",
          contactPhone: "",
          relationship: "",
          day: "",
          month: "",
          year: "",
        },
      ]);
    }
  };

  const updateAccountManagerForm = (index, key, value) => {
    const newForms = [...accountManagerForms];
    newForms[index][key] = value;
    setAccountManagerForms(newForms);

    const newErrorMessages = { ...errorMessages };
    if (newErrorMessages[index]) {
      delete newErrorMessages[index][key];
      if (key === "day" || key === "month" || key === "year") {
        if (
          !newForms[index].day ||
          !newForms[index].month ||
          !newForms[index].year
        ) {
          newErrorMessages[index].dateOfBirth =
            "Complete date of birth is required";
        } else {
          delete newErrorMessages[index].dateOfBirth;
        }
      }
      if (Object.keys(newErrorMessages[index]).length === 0) {
        delete newErrorMessages[index];
      }
      setErrorMessages(newErrorMessages);
    }
  };

  const handleInputChange = (setter, field) => (event) => {
    setter(event.target.value);

    const newErrorMessages = { ...errorMessages };
    if (newErrorMessages[field]) {
      delete newErrorMessages[field];
      setErrorMessages(newErrorMessages);
    }
  };

  const removeAccountManagerForm = (index) => {
    const newForms = accountManagerForms.filter((_, i) => i !== index);
    setAccountManagerForms(newForms);

    const newErrorMessages = { ...errorMessages };
    delete newErrorMessages[index];
    setErrorMessages(newErrorMessages);
  };

  const validateAccountManager = (manager) => {
    const errors = {};
    if (!manager.firstName) errors.firstName = "First name is required";
    if (!manager.lastName) errors.lastName = "Last name is required";
    if (!manager.contactPhone)
      errors.contactPhone = "Contact phone is required";
    if (!manager.relationship) errors.relationship = "Relationship is required";
    if (!manager.day || !manager.month || !manager.year)
      errors.dateOfBirth = "Complete date of birth is required";
    return errors;
  };

  const handleSaveAndContinue = async () => {
    let hasErrors = false;
    const newErrorMessages = {};

    accountManagerForms.forEach((manager, index) => {
      const errors = validateAccountManager(manager);
      if (Object.keys(errors).length > 0) {
        newErrorMessages[index] = errors;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrorMessages(newErrorMessages);
      return;
    }

    const formattedAccountManagerForms = accountManagerForms.map((form) => ({
      relationship: form.relationship,
      firstName: form.firstName,
      lastName: form.lastName,
      contactPhone: form.contactPhone,
      dateOfBirth: `${form.year}-${form.month}-${form.day}`,
    }));

    const updates = {
      accountManagerDetails: formattedAccountManagerForms,
    };

    console.log("Updates to be sent:", JSON.stringify(updates, null, 2));

    try {
      const response = await axios.patch(
        `${baseURL}/v1/client-users/${clientUser.id}`,
        updates,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data) {
        console.log("Client user updated successfully:", response.data);
        setShowModal(true);
      }
    } catch (error) {
      if (error.response) {
        console.error("Error updating client user:", error.response.data);
      } else {
        console.error("Error updating client user:", error.message);
      }
    }
  };

  return (
    <div className="personal-details-container">
      {showModal && <ProfileUpdateSuccessModal />}
      <h3>Personal Details</h3>
      <label>First Name</label>
      <input type="text" value={firstName} onChange={handleInputChange(setFirstName)} />

      <label>Last Name</label>
      <input type="text" value={lastName} onChange={handleInputChange(setLastName)} />

      <label>Phone Number</label>
      <input type="text" value={contactPhone} onChange={handleInputChange(setContactPhone)} />

      <label>Address</label>
      <input type="text" value={address} onChange={handleInputChange(setAddress)} />

      <label>Date of Birth</label>
      <div className="personal-details-dob-container">
        <div className="dob-dropdown-container">
        <Dropdown
          options={Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1}`, value: String(i + 1).padStart(2, "0") }))}
          onChange={(values) => setDay(values[0]?.value)}
          values={day ? [{ label: day, value: day }] : []}
          placeholder="Day"
          style={customDropdownStyles(isMobile)}
          contentRenderer={dropdownContentRenderer}
          itemRenderer={noBorderItemRenderer}
        />
        <Dropdown
          options={monthYearData.months.map((m, idx) => ({ label: m, value: String(idx + 1).padStart(2, "0") }))}
          onChange={(values) => setMonth(values[0]?.value)}
          values={month ? [{ label: monthYearData.months[parseInt(month, 10) - 1], value: month }] : []}
          placeholder="Month"
          style={customDropdownStyles(isMobile)}
          contentRenderer={dropdownContentRenderer}
          itemRenderer={noBorderItemRenderer}
        />
        <Dropdown
          options={monthYearData.years.map((y) => ({ label: y, value: y }))}
          onChange={(values) => setYear(values[0]?.value)}
          values={year ? [{ label: year, value: year }] : []}
          placeholder="Year"
          style={customDropdownStyles(isMobile)}
          contentRenderer={dropdownContentRenderer}
          itemRenderer={noBorderItemRenderer}
        />
        </div>
        <div className="age-display-container">Age <span className="age-arrow">➤</span> <strong>{age}</strong></div>
      </div>

      <label>Gender</label>
      <Dropdown
        options={genderOptions}
        onChange={(values) => setSelection(values[0]?.value)}
        values={selection ? [{ label: selection, value: selection }] : []}
        placeholder="Select"
        style={customDropdownStyles(isMobile)}
        contentRenderer={dropdownContentRenderer}
        itemRenderer={noBorderItemRenderer}
      />

      {accountManagerForms.map((form, idx) => {
        const isLast = idx === accountManagerForms.length - 1;
        return (
        <div key={idx} className="relationship-details" ref={isLast ? lastFormRef : null}>
          <h3>Person managing your account</h3>

          <label>Relationship</label>
          <Dropdown
            options={relationshipOptions}
            onChange={(values) => updateAccountManagerForm(idx, "relationship", values[0]?.value)}
            values={form.relationship ? [{ label: form.relationship, value: form.relationship }] : []}
            placeholder="Select"
            style={customDropdownStyles(isMobile)}
            contentRenderer={dropdownContentRenderer}
            itemRenderer={noBorderItemRenderer}
          />

          <label>First Name</label>
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => updateAccountManagerForm(idx, "firstName", e.target.value)}
          />

          <label>Last Name</label>
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => updateAccountManagerForm(idx, "lastName", e.target.value)}
          />

          <label>Phone Number</label>
          <input
            type="text"
            value={form.contactPhone}
            onChange={(e) => updateAccountManagerForm(idx, "contactPhone", e.target.value)}
          />

          <label>Date of Birth</label>
          <div className="personal-details-dob-container">
            <Dropdown
              options={Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1}`, value: String(i + 1).padStart(2, "0") }))}
              onChange={(values) => updateAccountManagerForm(idx, "day", values[0]?.value)}
              values={form.day ? [{ label: form.day, value: form.day }] : []}
              placeholder="Day"
              style={customDropdownStyles(isMobile)}
              contentRenderer={dropdownContentRenderer}
              itemRenderer={noBorderItemRenderer}
            />
            <Dropdown
              options={monthYearData.months.map((m, index) => ({ label: m, value: String(index + 1).padStart(2, "0") }))}
              onChange={(values) => updateAccountManagerForm(idx, "month", values[0]?.value)}
              values={form.month ? [{ label: monthYearData.months[parseInt(form.month, 10) - 1], value: form.month }] : []}
              placeholder="Month"
              style={customDropdownStyles(isMobile)}
              contentRenderer={dropdownContentRenderer}
              itemRenderer={noBorderItemRenderer}
            />
            <Dropdown
              options={monthYearData.years.map((y) => ({ label: y, value: y }))}
              onChange={(values) => updateAccountManagerForm(idx, "year", values[0]?.value)}
              values={form.year ? [{ label: form.year, value: form.year }] : []}
              placeholder="Year"
              style={customDropdownStyles(isMobile)}
              contentRenderer={dropdownContentRenderer}
              itemRenderer={noBorderItemRenderer}
            />
          </div>
          <div className="add-remove-buttons-wrapper">
            {accountManagerForms.length < 3 && (
              <div className="add-person-container">
                <button onClick={addAccountManagerForm}>
                  + Add person
                </button>
              </div>
            )}
            <div className="delete-button-container">
              <button onClick={() => removeAccountManagerForm(idx)}>
                Remove
              </button>
            </div>
          </div>
        </div>
        );
      })}

      <div className="personal-details-save">
        <button onClick={handleSaveAndContinue}>Save and Continue</button>
      </div>
    </div>
  );
};

export default PersonalDetailsComponent;
